/* eslint-disable react/jsx-no-bind */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Route, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { gql, useQuery } from '@apollo/client';
import dayjs from 'dayjs';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import usePageTracking from './utils/usePageTracking';

import WebAppContext from './utils/webAppContext';
import SideBar from './organisms/SideBar';
import TopBar from './organisms/TopBar';
import AlertDashboardPage from './pages/AlertDashboardPage';
import ApiAccessPage from './pages/APIAccessPage';
import BarnListPage from './pages/BarnListPage';
import BarnPage from './pages/BarnPage';
import MapPage from './pages/MapPage';
import NotFoundPage from './pages/NotFoundPage';
import SettingsPage from './pages/SettingsPage';
import ExportsPage from './pages/ExportsPage';
import FirmwarePage from './pages/FirmwarePage';
import DevicePage from './pages/DevicePage';
import TLVPage from './pages/TLVPage';
import RawDataPage from './pages/RawDataPage';
import AccuracyScorePage from './pages/AccuracyScorePage';
import FaultCreatorPage from './pages/FaultCreatorPage';
import FaultCodePage from './pages/FaultCodePage';
import FeedFramePage from './pages/FeedFramePage';
import DeviceStatusPage from './pages/DeviceStatusPage';
import FeedOrdersPage from './pages/FeedOrdersPage';
import MockBarnPage from './pages/MockBarnPage';
import AllBinsPage from './pages/AllBinsPage';
import CommandPalette from './organisms/CommandPalette';
import { algorithmVersionAtom, featureFlagsAtom } from './utils/jotaiAtoms';
import useAppBanner from './utils/hooks/useAppBanner/useAppBanner';
import { identify, initClarity } from './utils/clarityHelpers';
import useUser from './utils/hooks/useUser';
import Banner from './molecules/Banner';
import { useFakeQuery } from './organisms/FeatureFlagManager/FeatureFlagManager';
import { setupSentryRoutes, setSentryUser } from './utils/setupSentry';
import './App.scss';
import FeedFloErrorBoundary from './organisms/FeedFloErrorBoundary';
import WorkshopPage from './pages/WorkshopPage';
import LiveFeedFramePage from './pages/LiveFeedFramePage';
import { PrivilegeProvider } from '@feedflo/access-control';
import OrgManagementPage from './pages/OrgManagementPage';
import UserDetailPage from './pages/UserDetailPage/UserDetailPage';
import AlgorithmVersionsPage from './pages/AlgorithmVersionsPage/AlgorithmVersionsPage';
import AllUsersPage from './pages/AllUsersPage';
import DeviceMonitorPage from './pages/DeviceMonitorPage';
import CrashReportsMonitorPage from './pages/CrashReportsMonitorPage';
import MessageHistoryPage from './pages/MessageHistoryPage';
import LargestFeedframesPage from './pages/LargestFeedframesPage';
const SentryRoutes = setupSentryRoutes();

const ALGORITHM_OVERRIDE = import.meta.env.VITE_ALGORITHM_OVERRIDE;

const APP_GQL = gql`
  query AppGlobalInfoQuery($userID: uuid!) {
    user: user_by_pk(id: $userID) {
      is_metric
    }
  }
`;

export default function App() {
  let location = useLocation();
  const { user, loading: userLoading } = useUser();
  const { data: featuresData, loading: featuresLoading } = useFakeQuery(user?.isStaff, {
    skip: userLoading,
  }); // faked
  const [featureFlags, setFeatureFlags] = useAtom(featureFlagsAtom); // the array is an atom
  const [visibleSideBar, setVisibleSideBar] = useState(false);
  const [hideUIChrome, setHideUIChrome] = useState(false);
  const [isMetric, setIsMetric] = useState(false);
  const [algorithmVersion, setAlgorithmVersion] = useAtom(algorithmVersionAtom);
  const viewportRef = useRef();
  const { show: showBanner, message: bannerMessage } = useAppBanner();
  const menuButtonRef = useRef(null);

  useEffect(() => {
    dayjs.tz.setDefault(user.timezone);
  }, [user.timezone]);

  const { loading, data, error } = useQuery(APP_GQL, {
    fetchPolicy: 'no-cache',
    variables: {
      userID: user?.id,
    },
    skip: !user?.id,
  });

  useEffect(() => {
    // handle errors
    if (error) {
      console.error(error);
    }
    // If the env var has been used, set it
    if (ALGORITHM_OVERRIDE && !algorithmVersion) {
      setAlgorithmVersion(ALGORITHM_OVERRIDE);
    }

    if (!loading) {
      const isMetric = data?.user?.is_metric;
      setIsMetric(isMetric || false);
    }
  }, [loading, data, error, algorithmVersion, setAlgorithmVersion]);

  useEffect(() => {
    // call identify every page change but only with a logged in user
    if (user) {
      // use the document title since that's a nice clean string to identify
      // pages without including search params or UUIDs
      identify(user.id, document.title);
      setSentryUser(user.id, user.isStaff, user.organizations);
    }
  }, [user, location]);

  useEffect(() => {
    // Set up Feature Flags
    // TODO: Make this a util function/hook/component and actually pull from the DB?

    // If the lengths are the same we're going to assume with our fakeResponse that they're the same
    // No need to re-fetch and cause an infinite re-render loop
    // Replace with async code if moved to the DB though!
    // Only update if the user is fully loaded and if we have more features now than locally.
    // I could improve this logic in the future
    if (user && !userLoading && !featuresLoading && featuresData && featuresData?.length > featureFlags?.length) {
      setFeatureFlags(featuresData);
    }
  }, [featuresData, featuresLoading, user]);

  useEffect(() => {
    // One-Time Setup Calls
    initClarity();

    // provide a way to change the alg version when live! With some minor checks
    window.setAlgorithmVersion = (version) => {
      if (!version) {
        console.error('You must pass an algorithm version');
        return;
      }
      if (typeof version !== 'string') {
        console.error('Algorithm version must be a string');
        return;
      }
      setAlgorithmVersion(version);
    };
  }, []);

  const toggleSideBar = useCallback(() => {
    setVisibleSideBar(!visibleSideBar);
  }, [visibleSideBar]);

  const onClickOutside = useCallback(() => {
    setVisibleSideBar(false);
  }, []);

  usePageTracking();

  return (
    <FeedFloErrorBoundary>
      <PrivilegeProvider userId={user?.id}>
        <div className="App">
          {showBanner && <Banner className="App-notification" message={bannerMessage} />}
          <CommandPalette />
          <WebAppContext.Provider value={{ isMetric, setIsMetric }}>
            {!hideUIChrome && <TopBar menuButtonRef={menuButtonRef} onMenuButton={toggleSideBar} />}
            {!hideUIChrome && (
              <SideBar
                open={visibleSideBar}
                onClickOutside={onClickOutside}
                devInfoClassName={algorithmVersion && 'SideBarView-devInfo--updated'}
                menuButtonRef={menuButtonRef}
              />
            )}
            <div className="rightSide">
              <div className={`pageSpace customScroll ${!hideUIChrome ? 'UIChrome' : ''}`} ref={viewportRef}>
                <FeedFloErrorBoundary>
                  <SentryRoutes>
                    <Route exact path="/" element={<BarnListPage />} />
                    <Route path="/settings" element={<SettingsPage />} errorElement={<>Testing</>} />
                    <Route path="/b/:barnId/*" element={<BarnPage />} />
                    <Route path="/barns/:barnId/*" element={<BarnPage />} />
                    <Route path="/alerts" element={<AlertDashboardPage viewportRef={viewportRef} />} />
                    <Route path="/map" element={<MapPage />} />
                    <Route path="/exports" element={<ExportsPage />} />
                    <Route path="/firmware" element={<FirmwarePage />} />
                    <Route path="/apiAccess" element={<ApiAccessPage />} />
                    <Route path="/devices" element={<DevicePage />} />
                    <Route path="/deviceStatus" element={<DeviceStatusPage />} />
                    <Route
                      path="/liveFeedFrame/:serial"
                      element={<LiveFeedFramePage hideUIChrome={setHideUIChrome} />}
                    />
                    <Route path="/FeedDesk" element={<FeedOrdersPage />} />
                    <Route path="/rawData/:deviceId" element={<RawDataPage />} />
                    <Route path="/tlv/:dataId" element={<TLVPage />} />
                    <Route path="/accuracyScorePage" element={<AccuracyScorePage />} />
                    <Route path="/simulations/barn" element={<MockBarnPage />} />
                    <Route path="/faults/create" element={<FaultCreatorPage />} />
                    <Route path="/fault/:faultId/*" element={<FaultCodePage />} />
                    <Route path="/faults/:faultId/*" element={<FaultCodePage />} />
                    <Route path="/fault/:faultId/*" element={<FaultCodePage />} />
                    <Route path="/ff/:id" element={<FeedFramePage />} />
                    <Route path="/workshop" element={<WorkshopPage />} />
                    <Route path="/all-bins" element={<AllBinsPage />} />
                    <Route path="/orgs/:id/*" element={<OrgManagementPage />} />
                    <Route path="/orgs/*" element={<OrgManagementPage />} />
                    <Route path="/users/:id/*" element={<UserDetailPage />} />
                    <Route path="/users" element={<AllUsersPage />} />
                    <Route path="/staff/algorithm-versions" element={<AlgorithmVersionsPage />} />
                    <Route path="/staff/device-monitor" element={<DeviceMonitorPage viewportRef={viewportRef} />} />
                    <Route path="/staff/crash-reports-monitor" element={<CrashReportsMonitorPage />} />
                    <Route path="/message-history" element={<MessageHistoryPage />} />
                    <Route path="/staff/largest-feedframes" element={<LargestFeedframesPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </SentryRoutes>
                </FeedFloErrorBoundary>
              </div>
            </div>
            <ToastContainer
              position="bottom-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              enableMultiContainer
            />
          </WebAppContext.Provider>
        </div>
      </PrivilegeProvider>
    </FeedFloErrorBoundary>
  );
}
